<script lang="ts" setup>
import { computed, nextTick, onMounted, ref, watch, Teleport } from "vue"
import { FilterMatchMode } from "@primevue/core/api"
import { useI18n } from "vue-i18n"
import { ColumnConfig } from "@types"
import { useVModel } from "@vueuse/core"
import { isArray } from "lodash-es"
import { useClipboard } from "@vueuse/core"

type Props = {
    /**
     * Nom de l'dentifiant unique
     */
    dataKey: string
    /**
     * Liste des colonnes à afficher
     */
    columns: ColumnConfig[]
    /**
     * Liste des valeurs à afficher dans le tableau
     */
    value: any[]
    /**
     * Liste des lignes sélectionnées
     */
    selection?: any[]
    /**
     * Indique si les lignes sont sélectionnables
     */
    selectable?: boolean
    /**
     * Indique si une pagination doit être affichée
     */
    paginator?: boolean
    /**
     * Nombre de lignes par page
     */
    rows?: number
    /**
     * Liste des options de nombre de lignes par page
     */
    rowsPerPageOptions?: number[]
    /**
     * Indique si le tableau est scrollable
     */
    scrollable?: boolean
    /**
     * Indique si une barre de recherche doit être affichée
     */
    search?: boolean
    /**
     * Champs additionnels (hors colonnes) inclus dans le filtre global lorsque `search` est actif
     */
    searchGlobalExtraFields?: string[]
    /**
     * Identifiant du DOM où afficher la barre de recherche
     */
    teleportSearch?: string
    /**
     * Largeurs des lignes
     */
    size?: string
    /**
     * Indique si le paginator est petit
     */
    smallPaginator?: boolean
    /**
     * Message affiché lorsque le tableau est vide
     */
    emptyMessage?: string
    /**
     * Lignes du tableau actuellement étendues
     */
    expandedRows?: Record<string, boolean>
    /**
     * Mode lazy (pagination côté serveur) : total d'enregistrements pour le paginator
     */
    totalRecords?: number
    /**
     * Mode lazy : données chargées par le parent, @page émis au changement de page
     */
    lazy?: boolean
    /**
     * Mode lazy : index de la première ligne affichée (pour synchroniser le paginator)
     */
    first?: number
    /**
     * Affiche l'état de chargement du tableau (overlay PrimeVue DataTable)
     */
    loading?: boolean
    /**
     * Classe CSS à appliquer à la cellule
     */
    cellClass?: string
}

const props = withDefaults(defineProps<Props>(), {
    selectable: false,
    paginator: false,
    rows: 5,
    rowsPerPageOptions: () => [5, 10, 15],
    scrollable: false,
    search: false,
    searchGlobalExtraFields: () => [],
    selection: () => [],
    teleportSearch: "",
    size: "md",
    smallPaginator: false,
    emptyMessage: undefined,
    expandedRows: () => ({}),
    totalRecords: undefined,
    lazy: false,
    first: undefined,
    loading: false,
    cellClass: "",
})

const emit = defineEmits([
    "update:selection",
    "click:popup",
    "click:cell",
    "update:expandedRows",
    "update:filters",
    "page",
])

// ============================================
// Gestion du tableau
// ============================================

/**
 * Liste des lignes sélectionnées
 */
const selectedRows = useVModel(props, "selection", emit)
/**
 * Lignes du tableau actuellement étendues
 */
const expandedRows = useVModel(props, "expandedRows", emit, {
    defaultValue: {},
})
/**
 * Indique si toutes les lignes sont sélectionnées
 */
const isSelectAll = computed(
    () => selectedRows.value.length === props.value.length
)

/**
 * Propriétés à passer au composant BaseDataTable
 */
const getPropsForDataTable = computed(() => {
    const propsDataTable = {
        selectable: props.selectable,
        paginator: props.paginator,
        rows: props.rows,
        rowsPerPageOptions: props.rowsPerPageOptions,
        scrollable: props.scrollable,
        dataKey: props.dataKey,
        columnResizeMode: "fit",
        size: props.size,
        selectAll: isSelectAll.value,
    } as Record<string, any>

    if (props.lazy && props.totalRecords !== undefined) {
        propsDataTable["lazy"] = true
        propsDataTable["totalRecords"] = props.totalRecords
        if (props.first !== undefined) {
            propsDataTable["first"] = props.first
        }
    }

    if (props.scrollable) {
        propsDataTable["scrollHeight"] = "flex"
    }

    propsDataTable["loading"] = props.loading

    if (props.search) {
        const columnGlobalFields = props.columns
            /**
             * On ne prend que les champs qui sont affichés dans le tableau
             */
            .filter((column) => !column.expander)
            .map((column) => {
                // Si la colonne représente des chips, on filtre en concaténant les valeurs des chips
                if (column.chip) {
                    return `${column.field}__chips`
                }

                return column.field
            })
        propsDataTable["globalFilterFields"] = [
            ...columnGlobalFields,
            ...(props.searchGlobalExtraFields ?? []),
        ]
        propsDataTable["pt"] = {
            header: "base-table-header",
        }
    }

    return propsDataTable
})

function onUpdateSelection(value: any[]) {
    selectedRows.value = value
}

function onSelectAll() {
    selectedRows.value = isSelectAll.value ? [] : [...props.value]
}

// ============================================
// Gestion des filtres
// ============================================
/**
 * Initialise les filtres pour toutes les colonnes avec filterOptions
 */
function initializeFilters() {
    const initialFilters: Record<string, any> = {
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    }

    props.columns.forEach((column) => {
        if (column.filterOptions || column.filterable) {
            initialFilters[column.field] = {
                value: null,
                matchMode: FilterMatchMode.IN,
            }
        }
    })

    return initialFilters
}

/**
 * Liste des filtres sur le tableau
 */
const filters = ref(initializeFilters())

/**
 * En mode lazy, notifie le parent des changements de filtres pour refetch
 */
watch(
    filters,
    (newVal) => {
        if (props.lazy) {
            emit("update:filters", newVal)
        }
    },
    { deep: true }
)

// ============================================
// Gestion de la popup
// ============================================
const selectedRow = ref()

/**
 * Référence vers la popup affichant un sous tableau
 */
const popupDataTable = ref<
    | (HTMLElement & {
          show: (event?: any) => void
          hide: () => void
      })
    | null
>(null)

/**
 * Quand on clique sur un sous lien pour afficher une popup
 * @param event - Event de clique du bouton
 * @param data - Donnée de la ligne
 */
function onClickPopup(event: any, data: any, field: string, rowIndex: number) {
    if (!popupDataTable.value) return

    popupDataTable.value.hide()

    selectedRow.value = {
        ...data,
    }

    nextTick(() => {
        if (popupDataTable.value) popupDataTable.value.show(event)
        emit("click:popup", {
            event,
            data,
            field,
            rowIndex,
        })
    })
}

const { t }: { t: (key: string) => string } = useI18n({
    messages: {
        fr: {
            search: "Rechercher dans la liste",
        },
    },
})

const getValues = computed(() => {
    return props.value.map((item) => {
        const fields = props.columns.reduce(
            (acc, column) => {
                if (column.expander) {
                    return acc
                }
                if (column.chip) {
                    acc[column.field] = isArray(item[column.field])
                        ? item[column.field]
                        : [item[column.field]]
                    acc[`${column.field}__chips`] = (
                        item[column.field] ?? []
                    )?.join(", ")
                } else {
                    acc[column.field] = item[column.field]
                }
                return acc
            },
            {} as Record<string, any>
        )
        return {
            ...item,
            ...fields,
        }
    })
})

/**
 * Indique si le tableau a des filtres
 */
const hasFilters = computed(() => {
    return props.columns.some(
        (column) => column.filterOptions || column.filterable
    )
})

// =====================================================
// Gestion du overflow du text pour afficher le tooltip
// =====================================================

/**
 * Liste des célules du tableau
 */
const cells = ref<HTMLElement[]>([])
/**
 * Liste des états d'overflow des cellules
 */
const ellipsedCells = ref<{ value: boolean; id: string }[]>([])

/**
 * Calcule si le texte d'une cellule est en overflow
 */
function onPage(event: any) {
    checkEllipsis()
    emit("page", event)
}

async function checkEllipsis() {
    await nextTick()
    ellipsedCells.value = cells.value
        .map((cell: any) => {
            let isOverflowing = false

            const label = cell.querySelector(".base-table-link-label")

            if (!label) return { id: cell.id, value: false }

            isOverflowing = label.scrollWidth > label.clientWidth

            return {
                id: cell.id,
                value: isOverflowing,
            }
        })
        .filter(({ value }) => value)
}

/**
 * Indique si la cellule est en overflow et doit afficher un tooltip avec le texte complet
 * @param id - Identifiant de la cellule
 * @param value - Valeur de la cellule
 */
function isCellNeedEllipsedTooltip(id: string, value: string) {
    return ellipsedCells.value.find(({ id: _id }) => _id === id)?.value
        ? {
              value,
          }
        : null
}

/**
 * Calcule si le texte d'une cellule est en overflow
 */
onMounted(() => {
    checkEllipsis()
})

function onChangePage($event: any, callback: any, callbackParent: any) {
    callback($event)
    callbackParent($event)
}

// =====================================================
// Gestion du click sur les cellules
// =====================================================
const { copy } = useClipboard()
// référence vers le menu contextuel
const menu = ref<any>(null)
// information de la cellule sélectionnée pour le menu contextuel
const selectedCell = ref()
/**
 * Récupère le menu contextuel
 */
const getContextMenu = computed(() => {
    return [
        {
            label: "Ouvrir dans un autre onglet",
            icon: "pi pi-external-link",
            command: () => {
                onClickCell(new MouseEvent("click"), {
                    data: selectedCell.value.data,
                    field: selectedCell.value.field,
                    rowIndex: selectedCell.value.index,
                    _blank: true,
                })
            },
        },
        {
            label: "Copier",
            icon: "pi pi-copy",
            command: () => {
                copy(selectedCell.value.data?.[selectedCell.value.field])
            },
        },
    ]
})

/**
 * Quand le menu contextuel doit s'ouvrir
 * @param event - Event de clique
 * @param data - Donnée de la cellule
 * @param field - Nom du champ
 * @param index - Index de la ligne
 */
function onClickOpenMenu(
    event: any,
    {
        data,
        field,
        index,
    }: {
        data: any
        field: string
        index: number
    }
) {
    // On stocke les informations de la cellule sélectionnée
    selectedCell.value = {
        data,
        field,
        index,
    }
    if (menu.value) {
        menu.value.show(event)
    }
}

/**
 * Quand l'utilisateur clique sur une cellule.
 * Si dans l'event, la touche ctrl ou cmd est appuyée, on ouvre le lien dans un nouvel onglet.
 * @param event - Event de clique
 * @param data - Donnée de la cellule
 * @param field - Nom du champ
 * @param rowIndex - Index de la ligne
 * @param _blank - Ouvrir dans un nouvel onglet
 */
function onClickCell(
    event: MouseEvent,
    {
        data,
        field,
        rowIndex,
        _blank,
    }: {
        data: any
        field: string
        rowIndex: number
        _blank?: boolean
    }
) {
    let blank = _blank
    if (event.ctrlKey || event.metaKey) {
        blank = true
    }
    emit("click:cell", {
        data,
        field,
        rowIndex,
        _blank: blank,
    })
}

defineExpose({
    showTooltip: (index: number, field: string, value: string) => {
        return isCellNeedEllipsedTooltip(`link-${field}-${index}`, value)
    },
})
</script>

<template>
    <div
        class="base-table"
        :class="{ 'base-table--scrollable': props.scrollable }"
    >
        <BaseDataTable
            :value="getValues"
            :selection="selectedRows"
            v-model:filters="filters"
            v-model:expanded-rows="expandedRows"
            v-bind="getPropsForDataTable"
            :filter-display="hasFilters ? 'row' : null"
            :pt="{
                header: 'base-table-header bg-white! border-0! p-0!',
                bodyrow: 'base-table-row h-12!',
                thead: 'h-16!',
            }"
            @update:selection="onUpdateSelection"
            @select-all-change="onSelectAll"
            @page="onPage"
            @update:sort-order="checkEllipsis"
        >
            <!-- Barre de recherche -->
            <template #header v-if="search">
                <component
                    :is="props.teleportSearch ? Teleport : 'div'"
                    defer
                    :to="teleportSearch"
                >
                    <BaseInputWrapper
                        :label="t('search')"
                        input-id="input-search"
                        class="base-table__search max-w-[37.5rem] min-w-[18rem] ml-auto my-2 mr-2"
                    >
                        <BaseInputText
                            v-model="filters['global'].value"
                            input-id="input-search"
                            id="input-search"
                        />
                    </BaseInputWrapper>
                </component>
            </template>
            <!-- Colonne de sélection -->
            <BaseColumn
                v-if="selectable"
                selection-mode="multiple"
                header-style="width: 3rem"
            ></BaseColumn>
            <!-- Colonnes -->
            <BaseColumn
                v-for="(column, index) in columns"
                :key="index"
                :field="column.field"
                :header="column.header"
                :expander="column.expander"
                :sortable="column.sortable"
                :sort-field="column.sortField ? column.sortField : column.field"
                :style="column.style"
                :filter="!!(column.filterOptions || column.filterable)"
                :show-filter-menu="!column.filterOptions"
            >
                <!-- Filtre de colonne avec MultiSelect -->
                <template
                    v-if="column.filterOptions"
                    #filter="{ filterModel, filterCallback }"
                >
                    <BaseMultiSelect
                        v-model="filterModel.value"
                        :options="column.filterOptions"
                        option-label="label"
                        option-value="value"
                        placeholder="Tous"
                        display="chip"
                        :show-toggle-all="false"
                        @update:model-value="filterCallback()"
                        class="base-table__filter-multiselect"
                        :style="column.style"
                    >
                        <template #value="slotProps">
                            <span
                                v-if="slotProps.value && slotProps.value.length"
                            >
                                {{ slotProps.value.length }}
                                {{
                                    slotProps.value.length === 1
                                        ? "filtre appliqué"
                                        : "filtres appliqués"
                                }}
                            </span>
                            <span v-else>Tous</span>
                        </template>
                    </BaseMultiSelect>
                </template>
                <template v-if="!column.expander" #body="slotProps">
                    <div
                        class="base-table__cell flex items-center overflow-hidden"
                        :style="column.style"
                        :class="cellClass"
                        ref="cells"
                        :id="`link-${column.field}-${slotProps.index}`"
                    >
                        <div
                            v-if="
                                column.chip && !$slots[`cell_${column.field}`]
                            "
                            class="base-table__cell-chips flex gap-1 whitespace-nowrap max-w-[5rem] overflow-hidden text-ellipsis"
                            :style="column.style"
                        >
                            <BaseChip
                                v-for="(tag, indexTag) in slotProps.data[
                                    column.field
                                ]"
                                :key="indexTag"
                                :label="tag"
                            />
                        </div>

                        <BaseButton
                            v-else-if="
                                column.link && !$slots[`cell_${column.field}`]
                            "
                            :label="slotProps.data[column.field]"
                            variant="link"
                            class="base-table__link p-0 text-[var(--p-datatable-row-color)]! hover:text-[var(--p-datatable-row-color)]!"
                            :pt="{
                                label: 'base-table-link-label whitespace-nowrap overflow-hidden text-ellipsis font-normal!',
                            }"
                            v-tooltip.top="
                                isCellNeedEllipsedTooltip(
                                    `link-${column.field}-${slotProps.index}`,
                                    slotProps.data[column.field]
                                )
                            "
                            @contextmenu.prevent="
                                onClickOpenMenu($event, {
                                    data: slotProps.data,
                                    field: column.field,
                                    index: slotProps.index,
                                })
                            "
                            @click="
                                onClickCell($event, {
                                    data: slotProps.data,
                                    field: column.field,
                                    rowIndex: slotProps.index,
                                })
                            "
                        />
                        <p
                            v-else-if="!$slots[`cell_${column.field}`]"
                            class="pr-4 base-table-link-label whitespace-nowrap overflow-hidden text-ellipsis"
                            v-tooltip.top="
                                isCellNeedEllipsedTooltip(
                                    `link-${column.field}-${slotProps.index}`,
                                    slotProps.data[column.field]
                                )
                            "
                        >
                            {{ slotProps.data[column.field] }}
                        </p>
                        <slot
                            v-else
                            :name="`cell_${column.field}`"
                            v-bind="{
                                data: slotProps.data,
                                column,
                                index: slotProps.index,
                            }"
                        ></slot>
                        <BaseButton
                            v-if="
                                typeof column.subLink === 'function'
                                    ? column.subLink(slotProps.data)
                                    : column.subLink
                            "
                            size="small"
                            variant="link"
                            label="voir"
                            severity="secondary"
                            @click="
                                onClickPopup
                                    ? onClickPopup(
                                          $event,
                                          slotProps.data,
                                          column.field,
                                          slotProps.index
                                      )
                                    : null
                            "
                        />
                    </div>
                </template>
            </BaseColumn>
            <!-- Slot paginatorcontainer pour intégrer le paginator personnalisé plus petit -->
            <template
                #paginatorcontainer="propsPaginator"
                v-if="smallPaginator"
            >
                <BasePaginator
                    v-bind="propsPaginator"
                    :page="propsPaginator.page"
                >
                    <template
                        #container="{
                            first,
                            last,
                            page,
                            pageCount,
                            prevPageCallback,
                            nextPageCallback,
                            totalRecords,
                        }"
                    >
                        <div
                            class="flex items-center gap-4 border border-primary bg-transparent rounded-full w-full py-1 px-2 justify-between"
                        >
                            <BaseButton
                                icon="pi pi-chevron-left"
                                rounded
                                text
                                size="small"
                                @click="
                                    onChangePage(
                                        $event,
                                        prevPageCallback,
                                        propsPaginator.prevPageCallback
                                    )
                                "
                                :disabled="page === 0"
                            />
                            <div
                                class="text-color font-medium whitespace-nowrap"
                            >
                                <span class="hidden sm:block"
                                    >De {{ first }} à {{ last }} sur
                                    {{ totalRecords }}</span
                                >
                                <span class="block sm:hidden"
                                    >Page {{ page + 1 }} sur
                                    {{ pageCount }}</span
                                >
                            </div>
                            <BaseButton
                                icon="pi pi-chevron-right"
                                rounded
                                text
                                size="small"
                                @click="
                                    onChangePage(
                                        $event,
                                        nextPageCallback,
                                        propsPaginator.nextPageCallback
                                    )
                                "
                                :disabled="page === pageCount - 1"
                            />
                        </div>
                    </template>
                </BasePaginator>
            </template>

            <template #empty>
                <slot name="empty" v-if="$slots.empty"></slot>
                <p
                    v-else-if="emptyMessage"
                    class="text-gray-500 text-center my-2"
                >
                    {{ emptyMessage }}
                </p>
            </template>

            <template v-if="$slots.expansion" #expansion="slotProps">
                <slot name="expansion" v-bind="slotProps"></slot>
            </template>
        </BaseDataTable>
        <BasePopover ref="popupDataTable">
            <slot name="popup"></slot>
        </BasePopover>
        <BaseContextMenu ref="menu" :model="getContextMenu" />
    </div>
</template>

<style scoped>
/* ================================ */
/* FILTRE MULTISELECT */
/* ================================ */
.base-table__filter-multiselect {
    width: 100%;
    max-width: 100%;
}

.base-table__filter-multiselect :deep(.p-multiselect) {
    width: 100%;
    max-width: 100%;
}

.base-table__filter-multiselect :deep(.p-multiselect-label) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

/* ================================ */
/* STICKY HEADER (colonnes + filtres) en mode scrollable */
/* ================================ */
.base-table--scrollable :deep(thead) {
    position: sticky;
    top: 0;
    z-index: 2;
    background: var(--p-datatable-header-background, #fff);
    box-shadow: 0 1px 0 0 var(--p-datatable-border-color, #e5e7eb);
}

.base-table--scrollable :deep(thead th) {
    background: var(--p-datatable-header-background, #fff);
}
</style>
